import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { ConnectionService } from '../service/connection.service';
import { SwalService } from '../service/swal.service';
import { TranslateService } from '@ngx-translate/core';

declare global {
  interface Window {
    google: any;
  }
}

@Component({
  selector: 'app-google-signin-button',
  standalone: true,
  template: `<div class="widget" #widgetContainer></div>`,
  styles: [`
    /* Il widget è di Google (iframe): se per un origin non autorizzato o un
       errore di rete rende un fallback a larghezza fissa, non deve poter
       allargare la pagina oltre lo schermo. */
    :host { display: block; width: 100%; max-width: 100%; overflow: hidden; }
    .widget { display: flex; justify-content: center; max-width: 100%; overflow: hidden; }
  `],
})
export class GoogleSigninButtonComponent implements AfterViewInit, OnDestroy {
  @Input() clientId!: string;
  @Input() remember = false;
  @Output() success = new EventEmitter<any>();
  @Output() failure = new EventEmitter<any>();

  private pollInterval: any;

  constructor(
    private elRef: ElementRef,
    private connection: ConnectionService,
    private swal: SwalService,
    private translate: TranslateService,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.renderButton();
  }

  private renderButton(): void {
    // Aspetta che lo script Google Identity Services sia caricato
    this.pollInterval = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(this.pollInterval);
        window.google.accounts.id.initialize({
          client_id: this.clientId,
          // Il bottone Google gira fuori dalla zona di Angular: senza questo
          // wrapper i dati vengono salvati ma la UI non si aggiorna finché
          // non parte un altro giro di change detection (es. un refresh).
          callback: (response: { credential: string }) =>
            this.ngZone.run(() => this.onCredential(response)),
        });
        // GIS accetta solo una larghezza in pixel (min 200, max 400): la
        // calcolo dal contenitore reale per farla combaciare con gli input.
        const containerWidth = this.elRef.nativeElement.clientWidth || 320;
        const width = Math.min(400, Math.max(200, containerWidth));
        window.google.accounts.id.renderButton(
          this.elRef.nativeElement.querySelector('div'),
          { theme: 'outline', size: 'large', width },
        );
      }
    }, 100);
  }

  private onCredential(response: { credential: string }): void {
    this.postCredential(response.credential, false);
  }

  private postCredential(credential: string, confirmMerge: boolean): void {
    this.connection.post(
      '@auth/google',
      { credential, remember: this.remember, confirmMerge },
      this.onSuccess.bind(this),
      (error: any) => this.onFailure(error, credential),
    );
  }

  private onSuccess(result: any): void {
    this.success.emit(result);
  }

  private onFailure(error: any, credential: string): void {
    if (error?.error?.code === 'GOOGLE_LINK_CONFIRM') {
      this.swal
        .confirm(
          this.translate.instant('google_signin.link_confirm_title'),
          this.translate.instant('google_signin.link_confirm_text'),
          this.translate.instant('common.confirm'),
          this.translate.instant('common.cancel'),
        )
        .then((res: any) => {
          if (res.isConfirmed) this.postCredential(credential, true);
        });
      return;
    }
    this.failure.emit(error);
  }

  ngOnDestroy(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
    // Il widget Google (iframe/One Tap) vive fuori dall'albero DOM di
    // Angular: senza annullarlo esplicitamente può restare visibile/attivo
    // dopo la navigazione via router e intercettare click successivi.
    window.google?.accounts?.id?.cancel();
  }
}
